import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Drawer,
  Empty,
  Progress,
  Segmented,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import ReactECharts from "echarts-for-react";
import {
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  fmtQty,
  num,
} from "../ThreeMConsolidation/threeMShared";
import {
  PNL_COLORS,
  buildFyOptions,
  fmtMonth,
  fmtMonthLong,
  fyOf,
} from "../MonthlyPnl/pnlShared";

// Monthly schedule drawer, shared by the Material / Manpower / Machinery cost
// analysis reports: when an item is needed, month by month. Each month's figure
// is the item's allocation on a task scaled by that task's share of the month
// (scheduledQty / workQuantity), so this is the procurement-facing view of the
// same numbers the register totals.
//
// The sum of the months does not always equal the register row: tasks with no
// schedule, tasks with zero work quantity, and tasks scheduled to less or more
// than their full work quantity all move it. `coverage` carries the register
// side so the banner can state the difference rather than leave it unexplained.
//
// Report-agnostic by construction: the caller supplies a `fetchSchedule`
// thunk and a `fetchKey` that changes whenever the selection or filters do.
// Each report keeps its own identity plumbing; this owns only the presentation.

const ALL = "ALL";

export interface MonthlyScheduleMonthRow {
  month: string; // 'YYYY-MM-01'
  monthQuantity?: string;
  monthCost?: string;
  taskCount?: number;
}

export interface MonthlyScheduleCoverage {
  registerQuantity?: string;
  registerCost?: string;
  taskCount?: number;
  noScheduleTaskCount?: number;
  noScheduleCost?: string;
  noWorkQtyTaskCount?: number;
  noWorkQtyCost?: string;
}

interface DisplayMonth extends MonthlyScheduleMonthRow {
  qty: number;
  cost: number;
  cumQty: number;
  cumCost: number;
  share: number; // % of the visible months' cost
}

export interface MonthlyScheduleDrawerProps {
  open: boolean;
  /** Item name — becomes the drawer title. */
  itemName?: string;
  /** Item code / secondary identifier, shown beside the month span. */
  itemCode?: string;
  /** Head or source tag (e.g. MUCKING, TOOLS, PRW) — omitted when absent. */
  headLabel?: string;
  headColor?: string;
  uomName?: string;
  /** Noun used in error copy, e.g. "material" / "labour item". */
  entityLabel?: string;
  /** Returns the raw `{status, message, data}` API envelope. */
  fetchSchedule: () => Promise<any>;
  /** Refetch whenever this changes — encodes selection + active filters. */
  fetchKey: string;
  onClose: () => void;
  getContainer?: () => HTMLElement;
}

const MonthlyScheduleDrawer: React.FC<MonthlyScheduleDrawerProps> = ({
  open,
  itemName,
  itemCode,
  headLabel,
  headColor = "blue",
  uomName,
  entityLabel = "item",
  fetchSchedule,
  fetchKey,
  onClose,
  getContainer,
}) => {
  const [months, setMonths] = useState<MonthlyScheduleMonthRow[]>([]);
  const [coverage, setCoverage] = useState<MonthlyScheduleCoverage | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [fy, setFy] = useState<string>(ALL);
  const [metric, setMetric] = useState<"both" | "cost" | "qty">("both");

  // fetchSchedule is a fresh closure on every parent render, so it is
  // deliberately NOT a dependency — fetchKey is what says the data changed.
  useEffect(() => {
    if (!open || !fetchKey) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setMonths([]);
        setCoverage(null);
        setFy(ALL);
        const response = await fetchSchedule();
        if (cancelled) return;
        if (response?.status) {
          setMonths(response.data?.months || []);
          setCoverage(response.data?.coverage || null);
        } else {
          message.error(
            response?.message ||
              `Failed to load the ${entityLabel} monthly schedule`,
          );
        }
      } catch (err) {
        console.error("Error loading monthly schedule:", err);
        if (!cancelled)
          message.error(`Failed to load the ${entityLabel} monthly schedule`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchKey]);

  // FY chips only earn their space once the schedule spans more than one FY.
  const fyOptions = useMemo(
    () =>
      months.length
        ? buildFyOptions(months[0].month, months[months.length - 1].month)
        : [],
    [months],
  );

  // Cumulative is deliberately computed over the VISIBLE months, so a FY chip
  // rebases it instead of carrying a hidden opening balance.
  const visible = useMemo<DisplayMonth[]>(() => {
    const inScope =
      fy === ALL
        ? months
        : months.filter((m) => String(fyOf(m.month)) === fy);
    const totalCost = inScope.reduce((sum, m) => sum + num(m.monthCost), 0);
    let cumQty = 0;
    let cumCost = 0;
    return inScope.map((m) => {
      const qty = num(m.monthQuantity);
      const cost = num(m.monthCost);
      cumQty += qty;
      cumCost += cost;
      return {
        ...m,
        qty,
        cost,
        cumQty,
        cumCost,
        share: totalCost > 0 ? (cost / totalCost) * 100 : 0,
      };
    });
  }, [months, fy]);

  const visibleQty = visible.reduce((sum, m) => sum + m.qty, 0);
  const visibleCost = visible.reduce((sum, m) => sum + m.cost, 0);
  const scheduledCost = months.reduce((sum, m) => sum + num(m.monthCost), 0);
  const registerCost = num(coverage?.registerCost);
  const gap = registerCost - scheduledCost;
  const phasedPct = registerCost > 0 ? (scheduledCost / registerCost) * 100 : 0;

  // Below 1% the difference is per-row rounding, not a scheduling gap — saying
  // anything about it would be noise on an otherwise fully-phased item.
  const gapIsSignificant =
    registerCost > 0 && Math.abs(gap) >= registerCost * 0.01;

  const causes = useMemo(() => {
    const parts: string[] = [];
    const noSchedule = num(coverage?.noScheduleTaskCount);
    const noWorkQty = num(coverage?.noWorkQtyTaskCount);
    if (noSchedule > 0)
      parts.push(
        `${noSchedule} task${noSchedule === 1 ? " has" : "s have"} no monthly schedule (${fmtINR(num(coverage?.noScheduleCost))})`,
      );
    if (noWorkQty > 0)
      parts.push(
        `${noWorkQty} task${noWorkQty === 1 ? " has" : "s have"} no work quantity to phase against (${fmtINR(num(coverage?.noWorkQtyCost))})`,
      );
    return parts;
  }, [coverage]);

  const banner = () => {
    if (!coverage) return null;
    const taskCount = num(coverage.taskCount);
    if (taskCount === 0) return null;

    if (months.length === 0) {
      return (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="No monthly schedule yet"
          description={`None of the ${taskCount} task${taskCount === 1 ? "" : "s"} using this ${entityLabel} have a monthly production schedule, so ${fmtINR(registerCost)} cannot be phased across months yet.`}
        />
      );
    }
    if (!gapIsSignificant) {
      return (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${phasedPct.toFixed(0)}% of ${fmtINR(registerCost)} phased across ${months.length} month${months.length === 1 ? "" : "s"}`}
          description={`Across ${taskCount} task${taskCount === 1 ? "" : "s"}. These months account for the ${entityLabel}'s full register cost.`}
        />
      );
    }
    if (gap > 0) {
      return (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${fmtINR(gap)} of ${fmtINR(registerCost)} is not phased into any month`}
          description={
            causes.length
              ? causes.join("; ")
              : "Some tasks are scheduled for less than their full work quantity."
          }
        />
      );
    }
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={`Months total ${fmtINRSigned(-gap)} more than the register cost of ${fmtINR(registerCost)}`}
        description={`Some tasks are scheduled beyond 100% of their work quantity, so their ${entityLabel} is phased more than once.`}
      />
    );
  };

  // Combined mode plots cost as bars against the left rupee axis and quantity
  // as a line against a right axis in the item's own UOM. Normally mixing
  // units on one plot invites a misread, but here they measure the same thing
  // twice (cost = quantity x rate for a single item), so the shapes should
  // track — and where they DON'T is the useful signal: it means the rate mix
  // shifted that month. Axis labels are tinted to match their series so which
  // scale belongs to which is never in doubt.
  const chartOption = useMemo(() => {
    const both = metric === "both";
    const showCost = metric !== "qty";
    const showQty = metric !== "cost";
    const costData = visible.map((m) => m.cost);
    const qtyData = visible.map((m) => m.qty);

    // Single-metric modes keep the cumulative % on the right axis; in combined
    // mode quantity owns that axis and the table's Cum. columns carry it.
    const primary = metric === "qty" ? qtyData : costData;
    const primaryTotal = metric === "qty" ? visibleQty : visibleCost;
    let running = 0;
    const cumulativePct = primary.map((value) => {
      running += value;
      return primaryTotal > 0
        ? Number(((running / primaryTotal) * 100).toFixed(1))
        : 0;
    });

    const fmtSeriesValue = (name: string, value: number) => {
      if (name === "Quantity")
        return `${fmtQty(value)}${uomName ? ` ${uomName}` : ""}`;
      if (name === "Cumulative") return `${value}%`;
      return fmtINR(value);
    };

    const series: any[] = [];
    if (showCost)
      series.push({
        name: "Cost",
        type: "bar",
        data: costData,
        itemStyle: { color: PNL_COLORS.material, borderRadius: [3, 3, 0, 0] },
        barMaxWidth: 26,
      });
    if (showQty)
      series.push(
        both
          ? {
              name: "Quantity",
              type: "line",
              yAxisIndex: 1,
              data: qtyData,
              smooth: true,
              symbolSize: 4,
              lineStyle: { color: PNL_COLORS.machinery, width: 2 },
              itemStyle: { color: PNL_COLORS.machinery },
            }
          : {
              name: "Quantity",
              type: "bar",
              data: qtyData,
              itemStyle: {
                color: PNL_COLORS.material,
                borderRadius: [3, 3, 0, 0],
              },
              barMaxWidth: 26,
            },
      );
    if (!both)
      series.push({
        name: "Cumulative",
        type: "line",
        yAxisIndex: 1,
        data: cumulativePct,
        smooth: true,
        symbolSize: 4,
        lineStyle: { color: PNL_COLORS.margin, width: 2 },
        itemStyle: { color: PNL_COLORS.margin },
      });

    return {
      grid: { left: 8, right: 8, top: both ? 34 : 28, bottom: 8, containLabel: true },
      legend: both
        ? {
            show: true,
            top: 0,
            right: 0,
            itemHeight: 8,
            itemWidth: 12,
            textStyle: { fontSize: 11, color: "#595959" },
          }
        : { show: false },
      tooltip: {
        trigger: "axis",
        formatter: (params: any[]) => {
          const index = params[0]?.dataIndex ?? 0;
          const lines = params.map(
            (p) =>
              `${p.marker} ${p.seriesName}: ${fmtSeriesValue(p.seriesName, p.value)}`,
          );
          return `<b>${fmtMonthLong(visible[index]?.month || "")}</b><br/>${lines.join("<br/>")}`;
        },
      },
      xAxis: {
        type: "category",
        data: visible.map((m) => fmtMonth(m.month)),
        axisLabel: { fontSize: 11, color: "#8c8c8c", rotate: 45 },
      },
      yAxis: [
        {
          type: "value",
          axisLabel: {
            fontSize: 11,
            color: both ? PNL_COLORS.material : "#8c8c8c",
            formatter: (value: number) =>
              metric === "qty" ? fmtQty(value) : fmtINRCompact(value),
          },
          splitLine: { lineStyle: { color: "#f0f0f0" } },
        },
        both
          ? {
              type: "value",
              axisLabel: {
                fontSize: 11,
                color: PNL_COLORS.machinery,
                formatter: (value: number) => fmtQty(value),
              },
              splitLine: { show: false },
            }
          : {
              type: "value",
              min: 0,
              max: 100,
              axisLabel: {
                fontSize: 11,
                color: "#8c8c8c",
                formatter: (value: number) => `${value}%`,
              },
              splitLine: { show: false },
            },
      ],
      series,
    };
  }, [visible, metric, visibleCost, visibleQty, uomName]);

  const columns: ColumnsType<DisplayMonth> = [
    {
      title: "Month",
      dataIndex: "month",
      width: 100,
      render: (month: string) => (
        <span style={{ fontWeight: 500 }}>{fmtMonth(month)}</span>
      ),
    },
    {
      title: uomName ? `Qty (${uomName})` : "Qty",
      dataIndex: "qty",
      width: 130,
      align: "right",
      render: (qty: number) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmtQty(qty)}</span>
      ),
    },
    {
      title: "Cost",
      dataIndex: "cost",
      width: 120,
      align: "right",
      render: (cost: number) => (
        <Tooltip title={fmtINR(cost)}>
          <span
            style={{
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              cursor: "default",
            }}
          >
            {fmtINRCompact(cost)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "% of Scheduled",
      dataIndex: "share",
      width: 150,
      render: (share: number) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Progress
            percent={Math.min(100, Math.round(share * 10) / 10)}
            size="small"
            strokeWidth={4}
            showInfo={false}
            strokeColor={PNL_COLORS.material}
            style={{ flex: 1, minWidth: 60, margin: 0 }}
          />
          <span
            style={{
              fontSize: 12,
              color: "#595959",
              fontVariantNumeric: "tabular-nums",
              width: 42,
              textAlign: "right",
            }}
          >
            {share.toFixed(1)}%
          </span>
        </div>
      ),
    },
    {
      title: fy === ALL ? "Cum. Qty" : "Cum. Qty (FY)",
      dataIndex: "cumQty",
      width: 130,
      align: "right",
      render: (qty: number) => (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#8c8c8c" }}>
          {fmtQty(qty)}
        </span>
      ),
    },
    {
      title: fy === ALL ? "Cum. Cost" : "Cum. Cost (FY)",
      dataIndex: "cumCost",
      width: 120,
      align: "right",
      render: (cost: number) => (
        <Tooltip title={fmtINR(cost)}>
          <span
            style={{ fontVariantNumeric: "tabular-nums", color: "#8c8c8c" }}
          >
            {fmtINRCompact(cost)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Tasks",
      dataIndex: "taskCount",
      width: 74,
      align: "right",
      render: (value: any) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{num(value)}</span>
      ),
    },
  ];

  return (
    <Drawer
      title={
        itemName ? `${itemName} — Monthly Schedule` : "Monthly Schedule"
      }
      placement="right"
      width="min(1040px, 100vw)"
      onClose={onClose}
      open={open}
      destroyOnClose
      {...(getContainer ? { getContainer } : {})}
      footer={
        visible.length > 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
              color: "#022c57",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <span>
              {fy === ALL ? "Scheduled Total" : "FY Total"}
              <span
                style={{ marginLeft: 8, fontWeight: 400, color: "#8c8c8c" }}
              >
                {fmtQty(visibleQty)} {uomName || ""}
              </span>
            </span>
            <span>
              {fmtINR(visibleCost)}
              {fy !== ALL ? (
                <span
                  style={{ marginLeft: 10, fontWeight: 400, color: "#8c8c8c" }}
                >
                  all months {fmtINR(scheduledCost)}
                </span>
              ) : null}
            </span>
          </div>
        ) : null
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin />
        </div>
      ) : (
        <>
          {banner()}

          {months.length === 0 ? (
            num(coverage?.taskCount) === 0 ? (
              <Empty
                description={`This ${entityLabel} isn't used by any task in the current filters`}
              />
            ) : null
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {headLabel ? (
                    <Tag color={headColor} style={{ fontSize: 12 }}>
                      {headLabel}
                    </Tag>
                  ) : null}
                  <Tag color="blue" style={{ fontSize: 12 }}>
                    {months.length} month{months.length === 1 ? "" : "s"}
                  </Tag>
                  <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                    {fmtMonthLong(months[0].month)} –{" "}
                    {fmtMonthLong(months[months.length - 1].month)}
                    {itemCode ? ` · ${itemCode}` : ""}
                  </span>
                </div>
                {fyOptions.length > 1 ? (
                  <Segmented
                    size="small"
                    value={fy}
                    onChange={(value) => setFy(String(value))}
                    options={[
                      { label: "All", value: ALL },
                      ...fyOptions.map((option) => ({
                        label: option.label,
                        value: String(fyOf(option.fromMonth)),
                      })),
                    ]}
                  />
                ) : null}
              </div>

              <Card
                size="small"
                style={{ marginBottom: 12 }}
                title={
                  <span style={{ fontSize: 13 }}>
                    {metric === "both"
                      ? "Cost & quantity"
                      : metric === "cost"
                        ? "Cost"
                        : "Quantity"}{" "}
                    by month
                  </span>
                }
                extra={
                  <Segmented
                    size="small"
                    value={metric}
                    onChange={(value) =>
                      setMetric(value as "both" | "cost" | "qty")
                    }
                    options={[
                      { label: "Both", value: "both" },
                      { label: "Cost", value: "cost" },
                      { label: "Quantity", value: "qty" },
                    ]}
                  />
                }
              >
                <ReactECharts
                  option={chartOption}
                  style={{ height: 260 }}
                  notMerge
                />
              </Card>

              <Table<DisplayMonth>
                className="customTable"
                rowKey="month"
                columns={columns}
                dataSource={visible}
                pagination={false}
                bordered
                size="small"
                scroll={{ y: 360 }}
              />
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default MonthlyScheduleDrawer;
